import { Typography } from '@material-ui/core'
import Divider from '@material-ui/core/Divider'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import HoverTooltip from 'components/Feedbacks/HoverTooltip'
import { ListingDetail } from 'components/Models/listing'
import GridContainer from 'components/Surfaces/GridContainer'
import GridItem from 'components/Surfaces/GridItem'
import ViewBuildingType from 'components/Widgets/ProductListing/Details/GeneralInfo/ViewBuildingType'
import ViewDescription from 'components/Widgets/ProductListing/Details/GeneralInfo/ViewDescription'
import ViewDetail from 'components/Widgets/ProductListing/Details/GeneralInfo/ViewDetail'
import ViewName from 'components/Widgets/ProductListing/Details/GeneralInfo/ViewName'
import ViewPublishedDate from 'components/Widgets/ProductListing/Details/GeneralInfo/ViewPublishedDate'
import { removeFamilyNumberFromFamilyNameRegex } from 'components/Widgets/ProductListing/productsUtils'
import * as React from 'react'
import Truncate from 'react-truncate'
import { ListingDetailProperties } from './ListingDetailsProperties'

const useStyles = makeStyles((theme: Theme) => ({
    generalInfoContainer: {
        padding: theme.spacing(2),
        paddingRight: 0,
        display: 'block',
        color: '#474747',
        [theme.breakpoints.up('md')]: {
            paddingLeft: theme.spacing(2),
        },
    },
    font: { fontWeight: 500 },
    spacing: {
        marginTop: theme.spacing(1),
        alignItems: 'baseline',
    },
    layoutContainer: {
        overflowY: 'auto',
        width: '100%',
    },
    typographySpacing: {
        marginBottom: theme.spacing(1),
    },
    propertiesContainer: { marginTop: theme.spacing(2) },
    firmLogo: {
        width: 200,
        height: 60,
        objectFit: 'scale-down',
        objectPosition: 'left',
    },
    basicLayout: {
        marginBottom: theme.spacing(2),
    },
    buildingType: {
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(1.75),
    },
    listingLogo: { display: 'flex', alignItems: 'center' },
}))

interface ProductDetailsProps {
    productInfo: Partial<ListingDetail>
}

export const ProductDetails: React.FC<ProductDetailsProps> = ({
    productInfo,
}) => {
    const classes = useStyles()

    return (
        <GridContainer
            className={classes.generalInfoContainer}
            data-testid="productDetails"
        >
            <GridItem className={classes.listingLogo}>
                {productInfo.firmLogoUrl ? (
                    <HoverTooltip
                        text={productInfo.firmName}
                        children={
                            <img
                                src={productInfo.firmLogoUrl}
                                className={classes.firmLogo}
                                alt={productInfo.firmName}
                            />
                        }
                    />
                ) : (
                    <Truncate lines={2}>{productInfo.firmName}</Truncate>
                )}

            </GridItem>

            <GridItem className={classes.basicLayout}>
                <GridContainer direction="column">
                    <ViewDetail
                        name="Model #"
                        value={productInfo.model}
                        variant="h6"
                    />
                    <GridItem xs={12}>
                        <ViewName productName={productInfo.productName ?? ''} />
                    </GridItem>
                </GridContainer>
                <Divider />
            </GridItem>

            <GridContainer
                item
                direction="column"
                className={classes.layoutContainer}
            >
                <GridItem>
                    <ViewDetail
                        name="Category"
                        value={productInfo.categories?.join(', ')}
                        variant="h6"
                    />
                </GridItem>
                <GridItem>
                    <ViewDetail
                        name="Family Number"
                        value={productInfo.sectionNumber}
                        variant="h6"
                    />
                </GridItem>
                <GridItem>
                    <ViewDetail
                        name="Family"
                        value={productInfo.sectionName
                            ?.replace(removeFamilyNumberFromFamilyNameRegex, '')
                            .toUpperCase()}
                        variant="h6"
                    />
                </GridItem>

                <ViewDescription
                    longDescription={productInfo.longDescription}
                />

                <GridItem className={classes.buildingType}>
                    <ViewBuildingType
                        buildingTypes={productInfo.buildingTypes}
                    />
                </GridItem>

                <Divider />
                <GridContainer item>
                    <GridContainer
                        item
                        lg={3}
                        md={5}
                        xs={6}
                        className={clsx(
                            classes.spacing,
                            classes.typographySpacing
                        )}
                    >
                        {productInfo.firstPublished && (
                            <ViewPublishedDate
                                label="First Publish Date"
                                date={productInfo.firstPublished}
                            />
                        )}
                    </GridContainer>
                    <GridContainer
                        item
                        direction="column"
                        lg={3}
                        md={5}
                        xs={6}
                        className={clsx(
                            classes.spacing,
                            classes.font,
                            classes.typographySpacing
                        )}
                    >
                        {productInfo.lastUpdated && (
                            <ViewPublishedDate
                                label="Last Publish Date"
                                date={productInfo.lastUpdated}
                            />
                        )}
                    </GridContainer>
                </GridContainer>
                <Divider />
                <ViewDetail
                    name="Product Type"
                    value={productInfo.productType}
                    variant="h5"
                    lefSideFontWeight={500}
                />
                <GridContainer item className={classes.propertiesContainer}>
                    {productInfo.properties && (
                        <ListingDetailProperties
                            level={productInfo?.type ?? ''}
                            properties={productInfo.properties}
                        />
                    )}
                </GridContainer>
            </GridContainer>
        </GridContainer>
    )
}
